import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";

// Pages
import Landing from "./pages/Landing";
import TruckDetail from "./pages/TruckDetail";
import Checkout from "./pages/Checkout";
import OrderTracking from "./pages/OrderTracking";
import OrdersPage from "./pages/OrdersPage";
import Profile from "./pages/Profile";
import Wallet from "./pages/Wallet";
import VendorLanding from "./pages/VendorLanding";
import VendorDashboard from "./pages/VendorDashboard";
import VendorRegister from "./pages/VendorRegister";
import VendorPending from "./pages/VendorPending";
import AdminDashboard from "./pages/AdminDashboard";
import AuthCallback from "./pages/AuthCallback";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import Help from "./pages/Help";
import ProtectedRoute from "./components/ProtectedRoute";
import { INTERNAL_NAVIGATE_EVENT } from "./lib/internalNavigate";

function InternalNavigateBridge() {
  const navigate = useNavigate();
  useEffect(() => {
    const onNav = (e) => {
      const path = typeof e.detail?.path === "string" ? e.detail.path : "/";
      navigate(path, { replace: true });
    };
    window.addEventListener(INTERNAL_NAVIGATE_EVENT, onNav);
    return () => window.removeEventListener(INTERNAL_NAVIGATE_EVENT, onNav);
  }, [navigate]);
  return null;
}

function AppRouter() {
  const location = useLocation();
  
  // Handle auth callback fragments that may arrive on root URL (or cap_app native handoff on any path).
  if (
    location.hash?.includes('session_id=') ||
    location.hash?.includes('access_token=') ||
    location.search?.includes('code=') ||
    location.search?.includes('cap_app=')
  ) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/trucks/:truckId" element={<TruckDetail />} />
      <Route path="/checkout" element={
        <ProtectedRoute>
          <Checkout />
        </ProtectedRoute>
      } />
      <Route path="/orders" element={
        <ProtectedRoute>
          <OrdersPage />
        </ProtectedRoute>
      } />
      <Route path="/orders/:orderId" element={
        <ProtectedRoute>
          <OrderTracking />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <Profile />
        </ProtectedRoute>
      } />
      <Route path="/wallet" element={
        <ProtectedRoute>
          <Wallet />
        </ProtectedRoute>
      } />
      <Route path="/vendor" element={<VendorLanding />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <VendorDashboard />
        </ProtectedRoute>
      } />
      <Route path="/vendor/register" element={
        <ProtectedRoute>
          <VendorRegister />
        </ProtectedRoute>
      } />
      <Route path="/vendor/pending" element={
        <ProtectedRoute>
          <VendorPending />
        </ProtectedRoute>
      } />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/help" element={<Help />} />
      <Route path="/admin/vendor-dashboard/:vendorId" element={
        <ProtectedRoute requiredRole="admin">
          <VendorDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute requiredRole="admin">
          <AdminDashboard />
        </ProtectedRoute>
      } />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <InternalNavigateBridge />
      <AuthProvider>
        <CartProvider>
          <AppRouter />
          <Toaster position="top-center" richColors />
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
